// src/components/Pricing/PricingCard.tsx
import React from 'react';

// Launch offer disabled — DB stores the actual prices (v3.19: ₹49 Starter / ₹249 Pro / ₹849 Ultimate).
const LAUNCH_OFFER = {
  enabled: false,
  discountPercent: 0,
  promoPrices: {} as Record<string, { inr: number; usd: number }>,
};

interface PricingCardProps {
  tierName: string;
  tierCode: string;
  priceInr: number;
  priceUsd: number;
  creditsPerMonth: number;
  features: any;
  currency: 'INR' | 'USD';
  isCurrentTier?: boolean;
  isPopular?: boolean;
  isComingSoon?: boolean;
  onUpgrade?: () => void;
  upgradeLabel?: string;
  className?: string;
  billingLabel?: string;   // e.g., "/year" for yearly billing
  savingsNote?: string;    // e.g., "Save $90/yr"
}

const PricingCard: React.FC<PricingCardProps> = ({
  tierName,
  tierCode,
  priceInr,
  priceUsd,
  creditsPerMonth,
  features,
  currency,
  isCurrentTier = false,
  isPopular = false,
  isComingSoon = false,
  onUpgrade,
  upgradeLabel,
  className = '',
  billingLabel,
  savingsNote,
}) => {
  const price = currency === 'INR' ? priceInr : priceUsd;
  const currencySymbol = currency === 'INR' ? '₹' : '$';
  const isFree = price === 0;

  // Launch offer pricing
  const hasOffer = LAUNCH_OFFER.enabled && !isFree && LAUNCH_OFFER.promoPrices[tierCode];
  const promoPrice = hasOffer
    ? (currency === 'INR' ? LAUNCH_OFFER.promoPrices[tierCode].inr : LAUNCH_OFFER.promoPrices[tierCode].usd)
    : price;

  // Build features list based on tier
  const featuresList = [];

  // Credits
  if (creditsPerMonth === -1) {
    featuresList.push('Unlimited credits/month');
  } else {
    featuresList.push(`${creditsPerMonth.toLocaleString()} credits/month`);
  }

  // Trial period (for Basic tier)
  if (tierCode === 'basic' && features?.trial_days) {
    featuresList.push(`${features.trial_days}-day free trial`);
  }

  // AI Chat
  if (features?.chat) {
    featuresList.push('AI chat assistance');
  }

  // Code Execution
  if (features?.code_execution) {
    featuresList.push('Code execution');
  }

  // BookMate tutorials — tier-specific limits
  // basic tier deprecated in v3.19 — left here for legacy data only
  if (tierCode === 'pro' || tierCode === 'ultimate') {
    featuresList.push(`Refill anytime (${currency === 'INR' ? '\u20B999' : '$5'}/100 cr)`);
  } else {
    featuresList.push('Unlimited BookMate tutorials');
  }

  // Workflow (Pro and Ultimate only)
  if (tierCode === 'pro' || tierCode === 'ultimate') {
    featuresList.push('Workflow agents');
  }

  // Priority features
  if (features?.priority) {
    featuresList.push('Priority queue');
  }

  if (features?.priority_support) {
    featuresList.push('Priority support');
  }

  return (
    <div
      className={`relative border-2 rounded-2xl p-6 transition-all duration-300 flex flex-col ${
        isPopular
          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/20 shadow-lg shadow-purple-500/20'
          : isCurrentTier
          ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/20 shadow-lg shadow-green-500/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-xl'
      } ${className}`}
    >
      {/* Popular badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
            Most Popular
          </span>
        </div>
      )}

      {/* Current tier badge */}
      {isCurrentTier && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Current Plan
          </span>
        </div>
      )}

      <div className="text-center flex flex-col flex-grow">
        {/* Tier name */}
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {tierName}
        </h3>

        {/* Price */}
        <div className="mb-6">
          {isComingSoon ? (
            <div className="text-3xl font-bold text-gray-600 dark:text-gray-400">
              Coming Soon
            </div>
          ) : (
            <>
              {hasOffer && (
                <div className="mb-2">
                  <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Launch Offer — {LAUNCH_OFFER.discountPercent}% OFF
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-center">
                {hasOffer && (
                  <span className="text-xl font-semibold text-gray-400 dark:text-gray-500 line-through mr-2">
                    {currencySymbol}{price.toLocaleString()}
                  </span>
                )}
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                  {isFree ? 'Free' : `${currencySymbol}${promoPrice.toLocaleString()}`}
                </span>
                {!isFree && (
                  <span className="ml-2 text-gray-500 dark:text-gray-400 text-sm font-medium">
                    {billingLabel || '/month'}
                  </span>
                )}
              </div>
              {!isFree && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {billingLabel === '/year' ? 'Billed annually' : 'Billed monthly'}
                </p>
              )}
              {savingsNote && (
                <p className="text-xs text-green-500 font-semibold mt-1">
                  {savingsNote}
                </p>
              )}
              {!isFree && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                  Cancel anytime
                </p>
              )}
              {/* v3.19.7.1: removed the "Free for 7 days (200 credits) / Auto-renews after trial"
                  badge — that was the v3.19 Razorpay-trial-on-Pro UX. In v3.19.7+, the free
                  trial lives on the Starter tier (auto-granted at signup), and Pro is a
                  straight ₹249/mo paid subscription. No trial badge on Pro card. */}
            </>
          )}
        </div>

        {/* Features list */}
        <ul className="space-y-3 mb-8 text-left flex-grow">
          {featuresList.map((feature, index) => (
            <li key={index} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
              <svg
                className={`w-5 h-5 mr-2 flex-shrink-0 ${
                  isPopular
                    ? 'text-purple-600 dark:text-purple-400'
                    : isCurrentTier
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA button */}
        {isComingSoon ? (
          <button
            disabled
            className="w-full py-3 px-6 rounded-xl text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
          >
            Coming Soon
          </button>
        ) : isCurrentTier ? (
          <button
            disabled
            className="w-full py-3 px-6 rounded-xl text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-2 border-green-500 cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Current Plan
          </button>
        ) : (
          <button
            onClick={onUpgrade}
            className={`w-full py-3 px-6 rounded-xl text-sm font-semibold transition-all duration-200 ${
              isPopular
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transform hover:-translate-y-0.5'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transform hover:-translate-y-0.5'
            }`}
          >
            {upgradeLabel || 'Get Started'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PricingCard;
